import { useAuth0 } from "@auth0/auth0-react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { MerchantContext } from "contexts/MerchantContext";
import MerchantDomainBase from "entities/domain/admin/merchants/merchant-domain";
import { MerchantIntegrations } from "entities/domain/admin/merchants/merchant-integrations";
import ChannelDomain from "entities/domain/contacts/contact-domain";
import React, { useContext } from "react";
import { useAppSelector } from "redux/hooks";
import ChannelService from "services/channel";
import MerchantService, { UpdateMerchantPayload } from "services/merchant";

// Query keys
export const settingsMerchantQueryKey = (
  merchantId: number | null | undefined
) => ["settings-merchant", merchantId ?? null];

export const merchantQueryKey = (merchantId: number | null | undefined) => [
  "merchant",
  merchantId ?? null,
];

/**
 * Hook to fetch merchant data for Settings pages
 * Returns guaranteed non-null merchant - settings pages are inside PrivateRoute guard.
 */
export function useSettingsMerchant(staleTimeMs: number = 60_000): {
  data: MerchantDomainBase;
  isLoading: boolean;
  isFetching: boolean;
  isPending: boolean;
  error: Error | null;
} {
  // First check context - if we're inside PrivateRoute, merchant is guaranteed
  const contextMerchant = useContext(MerchantContext);

  const auth0Context = useAuth0();
  const { merchantId } = useAppSelector((state) => state.merchant);

  const query = useQuery<MerchantDomainBase | null, Error>({
    queryKey: settingsMerchantQueryKey(merchantId),
    enabled: Boolean(merchantId) && !contextMerchant,
    queryFn: async () => {
      if (!merchantId) return null;
      return MerchantService.getMerchant(auth0Context, merchantId);
    },
    staleTime: staleTimeMs,
  });

  // If we have context merchant (inside PrivateRoute), use it
  if (contextMerchant) {
    return {
      data: contextMerchant,
      isLoading: false,
      isFetching: false,
      isPending: false,
      error: null,
    };
  }

  // This hook should only be called inside PrivateRoute where merchant is guaranteed
  // The non-null assertion is safe because PrivateRoute guards against null merchant
  return {
    data: query.data!,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPending: query.isPending,
    error: query.error,
  };
}

/**
 * Hook to get channels from settings merchant snapshot
 */
export function useSettingsChannels() {
  const { data: merchant } = useSettingsMerchant();
  return merchant?.channels ?? [];
}

/**
 * Hook to get integrations from settings merchant snapshot
 */
export function useSettingsIntegrations(): MerchantIntegrations {
  const { data: merchant } = useSettingsMerchant();
  return merchant?.integrations ?? ({} as MerchantIntegrations);
}

/**
 * Hook to update merchant settings (logo, google place id, etc.)
 * Optimistically patches the settings merchant cache
 */
export function useUpdateMerchantSettings() {
  const auth0Context = useAuth0();
  const { merchantId } = useAppSelector((state) => state.merchant);
  const queryClient = useQueryClient();

  return useMutation<
    MerchantDomainBase | null,
    Error,
    UpdateMerchantPayload,
    { prevMerchant?: MerchantDomainBase | null }
  >({
    mutationFn: async (payload) => {
      if (!merchantId) return null;
      return MerchantService.updateMerchant(auth0Context, payload, merchantId);
    },
    onMutate: async (payload) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: settingsMerchantQueryKey(merchantId),
      });

      // Snapshot previous value
      const prevMerchant = queryClient.getQueryData<MerchantDomainBase | null>(
        settingsMerchantQueryKey(merchantId)
      );

      // Optimistically update
      if (prevMerchant) {
        const updatedMerchant = { ...prevMerchant };
        if (payload.logoUrl !== undefined) {
          updatedMerchant.externalMetadata = {
            ...prevMerchant.externalMetadata,
            logo: payload.logoUrl,
          };
        }
        if (payload.googlePlaceId !== undefined) {
          // Note: googlePlaceId is not a direct property, it's derived from location
          // We'll let the server response handle this properly
        }

        queryClient.setQueryData(
          settingsMerchantQueryKey(merchantId),
          updatedMerchant
        );
      }

      return { prevMerchant };
    },
    onError: (_err, _vars, context) => {
      // Rollback on error
      if (context?.prevMerchant && merchantId) {
        queryClient.setQueryData(
          settingsMerchantQueryKey(merchantId),
          context.prevMerchant
        );
      }
    },
    onSuccess: (updated) => {
      if (!updated || !merchantId) return;

      // Update cache with server response
      queryClient.setQueryData(settingsMerchantQueryKey(merchantId), updated);
    },
  });
}

/**
 * Hook to disable WhatsApp channel
 * Optimistically removes the WhatsApp channel from merchant.channels
 */
export function useDisableWhatsappChannel() {
  const auth0Context = useAuth0();
  const { merchantId } = useAppSelector((state) => state.merchant);
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    { phoneNumber: string },
    { prevMerchant?: MerchantDomainBase | null }
  >({
    mutationFn: async ({ phoneNumber }) => {
      if (!merchantId) return;
      await ChannelService.disableWhatsappChannel(
        auth0Context,
        phoneNumber,
        merchantId
      );
    },
    onMutate: async ({ phoneNumber }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: settingsMerchantQueryKey(merchantId),
      });

      // Snapshot previous value
      const prevMerchant = queryClient.getQueryData<MerchantDomainBase | null>(
        settingsMerchantQueryKey(merchantId)
      );

      // Optimistically remove WhatsApp channel
      if (prevMerchant) {
        const updatedChannels = prevMerchant.channels.filter(
          (ch) =>
            !(ch.channelName === "whatsapp" && ch.identifier === phoneNumber)
        );

        const updatedMerchant = {
          ...prevMerchant,
          channels: updatedChannels,
        };

        queryClient.setQueryData(
          settingsMerchantQueryKey(merchantId),
          updatedMerchant
        );
      }

      return { prevMerchant };
    },
    onError: (_err, _vars, context) => {
      // Rollback on error
      if (context?.prevMerchant && merchantId) {
        queryClient.setQueryData(
          settingsMerchantQueryKey(merchantId),
          context.prevMerchant
        );
      }
    },
    onSuccess: () => {
      // Invalidate to refetch fresh merchant data (channel removal may affect other state)
      if (merchantId) {
        queryClient.invalidateQueries({
          queryKey: settingsMerchantQueryKey(merchantId),
        });
      }
    },
  });
}

/**
 * Hook to assign SMS channel (used in onboarding)
 */
export function useAssignSmsChannel() {
  const auth0Context = useAuth0();
  const { merchantId } = useAppSelector((state) => state.merchant);
  const queryClient = useQueryClient();

  return useMutation<ChannelDomain, Error, void>({
    mutationFn: async () => {
      if (!merchantId) throw new Error("Merchant ID required");
      return ChannelService.assignSmsChannel(auth0Context, merchantId);
    },
    onSuccess: () => {
      // Invalidate merchant settings to refresh channels
      if (merchantId) {
        queryClient.invalidateQueries({
          queryKey: settingsMerchantQueryKey(merchantId),
        });
        queryClient.invalidateQueries({
          queryKey: merchantQueryKey(merchantId),
        });
      }
    },
  });
}

/**
 * Internal hook used ONLY by PrivateRoute to fetch merchant data during loading.
 * This returns nullable data because merchant may not be loaded yet.
 *
 * DO NOT use this hook in components - use useMerchant() instead.
 */
export function useMerchantQuery(
  merchantId?: number,
  staleTimeMs: number = 60_000
) {
  const auth0Context = useAuth0();
  const { merchantId: reduxMerchantId } = useAppSelector(
    (state) => state.merchant
  );
  const resolvedMerchantId = merchantId ?? reduxMerchantId;

  return useQuery<MerchantDomainBase | null, Error>({
    queryKey: merchantQueryKey(resolvedMerchantId),
    enabled: Boolean(resolvedMerchantId),
    queryFn: async () => {
      if (!resolvedMerchantId) return null;
      return MerchantService.getMerchant(auth0Context, resolvedMerchantId);
    },
    staleTime: staleTimeMs,
    // Keep previous merchant data while switching to prevent brief undefined state
    // that could cause components to unmount/remount
    placeholderData: keepPreviousData,
  });
}

/**
 * Hook to get merchant data - guaranteed non-null inside PrivateRoute.
 *
 * This hook should be used by components rendered inside PrivateRoute.
 * The merchant is guaranteed to exist because PrivateRoute only renders children
 * after the merchant is loaded.
 *
 * For code that runs OUTSIDE PrivateRoute (like App.tsx), use useMerchantQuery() instead.
 */
export function useMerchant(): {
  data: MerchantDomainBase;
  isLoading: false;
  isFetching: false;
  isPending: false;
  error: null;
} {
  const contextMerchant = useContext(MerchantContext);

  if (!contextMerchant) {
    // This should never happen if used correctly inside PrivateRoute
    // Return a placeholder to avoid crashing - the guard in PrivateRoute will prevent rendering
    return {
      data: null as unknown as MerchantDomainBase,
      isLoading: false,
      isFetching: false,
      isPending: false,
      error: null,
    };
  }

  return {
    data: contextMerchant,
    isLoading: false,
    isFetching: false,
    isPending: false,
    error: null,
  };
}

/**
 * Hook to update merchant (general use)
 * Optimistically updates the merchant cache
 */
export function useUpdateMerchant() {
  const auth0Context = useAuth0();
  const { merchantId } = useAppSelector((state) => state.merchant);
  const queryClient = useQueryClient();

  return useMutation<
    MerchantDomainBase | null,
    Error,
    UpdateMerchantPayload,
    { prevMerchant?: MerchantDomainBase | null }
  >({
    mutationFn: async (payload) => {
      if (!merchantId) return null;
      return MerchantService.updateMerchant(auth0Context, payload, merchantId);
    },
    onMutate: async (payload) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: merchantQueryKey(merchantId),
      });
      await queryClient.cancelQueries({
        queryKey: settingsMerchantQueryKey(merchantId),
      });

      // Snapshot previous values
      const prevMerchant = queryClient.getQueryData<MerchantDomainBase | null>(
        merchantQueryKey(merchantId)
      );

      // Optimistically update both caches
      if (prevMerchant) {
        const updatedMerchant = { ...prevMerchant };
        if (payload.logoUrl !== undefined) {
          updatedMerchant.externalMetadata = {
            ...prevMerchant.externalMetadata,
            logo: payload.logoUrl,
          };
        }

        queryClient.setQueryData(merchantQueryKey(merchantId), updatedMerchant);
        queryClient.setQueryData(
          settingsMerchantQueryKey(merchantId),
          updatedMerchant
        );
      }

      return { prevMerchant };
    },
    onError: (_err, _vars, context) => {
      // Rollback on error
      if (context?.prevMerchant && merchantId) {
        queryClient.setQueryData(
          merchantQueryKey(merchantId),
          context.prevMerchant
        );
        queryClient.setQueryData(
          settingsMerchantQueryKey(merchantId),
          context.prevMerchant
        );
      }
    },
    onSuccess: (updated) => {
      if (!updated || !merchantId) return;

      // Update both caches with server response
      queryClient.setQueryData(merchantQueryKey(merchantId), updated);
      queryClient.setQueryData(settingsMerchantQueryKey(merchantId), updated);
    },
  });
}
